import unionWith from 'lodash/unionWith';
import { filter, path } from 'ramda';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import { formatDateStringToUTC, getExclusiveEndDate } from '../../util/dates';
import { parse, filterSearchCountry } from '../../util/urlHelpers';
import { addToJourney } from '../../util/autopilot';
import { types as sdkTypes } from '../../util/sdkLoader';
import config from '../../config';

const { LatLng, LatLngBounds } = sdkTypes;

const MAX_SIMILAR_LISTINGS = 3;

const triggerIds = {
  'Aix-en-Provence': '0011',
  'Avignon': '0007',
  'Bordeaux': '0009',
  'Lyon': '0006',
  'Marseille': '0013',
  'Montpellier': '0001',
  'Nantes': '0005',
  'Nice': '0012',
  'Paris': '0014',
  'Perpignan': '0015',
  'Toulon': '0010',
  'Toulouse': '0008',
};

// ================ Action types ================ //

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

export const SEARCH_SIMILAR_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_SIMILAR_LISTINGS_SUCCESS';
export const SET_SIMILAR_LISTINGS = 'app/SearchPage/SET_SIMILAR_LISTINGS';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],
  searchMapListingIds: [],
  searchMapListingsError: null,
  similarListingIds: [],
};

const resultIds = data => data.data.map(l => l.id);

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_LISTINGS_REQUEST:
      return {
        ...state,
        searchMapListingsError: null,
      };
    case SEARCH_MAP_LISTINGS_SUCCESS: {
      const searchMapListingIds = unionWith(
        state.searchMapListingIds,
        resultIds(payload.data),
        (id1, id2) => id1.uuid === id2.uuid
      );
      return {
        ...state,
        searchMapListingIds,
      };
    }
    case SEARCH_MAP_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchMapListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };
    
    case SEARCH_SIMILAR_LISTINGS_SUCCESS: {
      const resultsIdsWithoutCurrentListing = filter(id => id.uuid !== payload.listingId.uuid, resultIds(payload.data))
      const sortedResultIds = resultsIdsWithoutCurrentListing.sort(() => 0.5 - Math.random());
      const similarListingIds = sortedResultIds.slice(0, MAX_SIMILAR_LISTINGS);
      return {
        ...state,
        similarListingIds: similarListingIds,
        searchInProgress: false,
      };
    }
    case SET_SIMILAR_LISTINGS: {
      const resultsIdsWithoutCurrentListing = filter(id => id.uuid !== payload.listingId.uuid, payload.data)
      const sortedResultIds = resultsIdsWithoutCurrentListing.sort(() => 0.5 - Math.random());
      const similarListingIds = sortedResultIds.slice(0, MAX_SIMILAR_LISTINGS);
      return {
        ...state,
        similarListingIds: similarListingIds,
      };
    }

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchMapListingsRequest = () => ({ type: SEARCH_MAP_LISTINGS_REQUEST });

export const searchMapListingsSuccess = response => ({
  type: SEARCH_MAP_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchMapListingsError = e => ({
  type: SEARCH_MAP_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchSimilarListingsSuccess = (response, listingId) => ({
  type: SEARCH_SIMILAR_LISTINGS_SUCCESS,
  payload: { data: response.data, listingId: listingId },
});

export const setSimilarListings = (response, listingId) => ({
  type: SET_SIMILAR_LISTINGS,
  payload: { data: response, listingId: listingId },
});

export const searchListings = (searchParams, search) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  if (search) {
    const { address } = parse(search);
    const city = filterSearchCountry(address);
    const email = path(['currentUser', 'attributes', 'email'], getState().user);
    const triggerId = triggerIds[city];
    addToJourney(triggerId, email);
  }

  const priceSearchParams = priceParam => {
    const inSubunits = value =>
      convertUnitToSubUnit(value, unitDivisor(config.currencyConfig.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesParam && values.length === 2;
    const startDate = hasValues ? values[0] : null;
    const isNightlyBooking = config.bookingUnitType === 'line-item/night';
    const endDate =
      hasValues && isNightlyBooking ? values[1] : hasValues ? getExclusiveEndDate(values[1]) : null;

    return hasValues
      ? {
          start: formatDateStringToUTC(startDate),
          end: formatDateStringToUTC(endDate),
          // Availability can be full or partial. Default value is full.
          availability: 'partial',
        }
      : {};
  };

  const { perPage, price, dates, ...rest } = searchParams;
  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);

  const params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    per_page: perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(searchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

export const searchMapListings = searchParams => (dispatch, getState, sdk) => {
  dispatch(searchMapListingsRequest(searchParams));

  const { perPage, ...rest } = searchParams;
  const params = {
    ...rest,
    per_page: perPage,
  };

  return sdk.listings
    .query(params)
    .then(response => {
      dispatch(addMarketplaceEntities(response));
      dispatch(searchMapListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchMapListingsError(storableError(e)));
      throw e;
    });
};

export const searchSimilarListings = listingId => async (dispatch, getState, sdk) => {
  const state = getState();
  const { currentPageResultIds ,searchParams } = state.SearchPage;
  if (searchParams && searchParams.city) {
    return dispatch(setSimilarListings(currentPageResultIds, listingId));
  } else {
    return null;
  }
};



// WEBPACK FOOTER //
// ./src/containers/SearchPage/SearchPage.duck.js